import { composePrice } from '@crm/utils'
import { ConditionalText, TableContainer, Text } from '@shared/components'
import { useTableSorter } from '@shared/hooks'
import { Col, Flex, Row, TableColumnsType, theme } from 'antd'
import { useMemo } from 'react'
import { useTranslation } from 'react-i18next'
import { Cell, Pie, PieChart, ResponsiveContainer, Tooltip } from 'recharts'

import { useStyles } from './styles'

type Props = {
  data: {
    name: string
    opportunity_count?: number
    total_value?: number
    currency?: string | null
    total: number
    color: string
  }[]
  chartHeight?: number
  chartText?: string
  emptyMessage?: string
  textColumn?: string
}

export const ReportsPieChart = ({
  data,
  chartHeight = 300,
  chartText = '',
  emptyMessage,
  textColumn
}: Props) => {
  const { t } = useTranslation()
  const { styles } = useStyles()
  const { token } = theme.useToken()
  const sorter = useTableSorter()

  const sortedData = useMemo(() => {
    if (!data || data.length === 0) return []
    return [...data].slice(0, 10)
  }, [data])

  const isEmpty = !data || data.length === 0

  const chartData = isEmpty
    ? [
        {
          name: t('main.no_data'),
          count: 1,
          color: token.colorTextTertiary
        }
      ]
    : data

  const total = data.reduce((sum, item) => sum + item.total, 0)
  const emptyDataMessage = emptyMessage || t('main.no_data')

  const columns: TableColumnsType = [
    {
      title: textColumn,
      dataIndex: 'name',
      key: 'name',
      render: (name) => (
        <ConditionalText condition={!!name}>{name}</ConditionalText>
      )
    },
    {
      title: t('lead.opportunities'),
      dataIndex: 'opportunity_count',
      key: 'opportunity_count',
      sorter: true,
      render: (opportunities) => (
        <ConditionalText condition={!!opportunities}>
          {opportunities}
        </ConditionalText>
      )
    },
    {
      title: t('task.value'),
      dataIndex: 'total_value',
      key: 'total_value',
      sorter: true,
      render: (total_value, record) => (
        <ConditionalText condition={!!total_value}>
          {composePrice(total_value, record.currency)}
        </ConditionalText>
      )
    }
  ]

  return (
    <Row justify="center" align="top">
      <Col sm={24}>
        <ResponsiveContainer width="100%" height={chartHeight}>
          <PieChart>
            <Pie
              data={chartData}
              dataKey="total_value"
              stroke="none"
              cx="50%"
              cy="50%"
              innerRadius={75}
              outerRadius={125}
            >
              {chartData.map((entry, index) => (
                <Cell key={`cell-${index}`} fill={entry.color} />
              ))}
            </Pie>

            <text
              x="50%"
              y={isEmpty ? '50%' : '47%'}
              textAnchor="middle"
              dominantBaseline="middle"
              fontSize={isEmpty ? '16' : '20'}
              fill={isEmpty ? token.colorTextSecondary : undefined}
            >
              {isEmpty ? emptyDataMessage : chartText}
            </text>

            {!isEmpty && (
              <text
                x="50%"
                y="55%"
                textAnchor="middle"
                dominantBaseline="middle"
                fontSize="30"
                fontWeight={600}
              >
                {total}
              </text>
            )}

            {!isEmpty && (
              <Tooltip
                content={({ active, payload }) => {
                  if (!active || !payload || !payload.length) return null
                  const { name, opportunity_count, total_value, currency } =
                    payload[0].payload

                  return (
                    <Flex vertical gap={4} className={styles.chartTooltip}>
                      <Text className={styles.tooltipText}>
                        {t('generic.name')}: {name}
                      </Text>
                      <Text className={styles.tooltipText}>
                        {t('lead.opportunities')}: {opportunity_count}
                      </Text>
                      <Text className={styles.tooltipText}>
                        {t('billing.total')}:{' '}
                        {composePrice(total_value, currency)}
                      </Text>
                    </Flex>
                  )
                }}
              />
            )}
          </PieChart>
        </ResponsiveContainer>
      </Col>

      <Col sm={24}>
        <TableContainer
          rowKey="name"
          data={{ results: sortedData }}
          columns={columns}
          showSearch={false}
          onChange={(_, __, result) => sorter(result)}
        />
      </Col>
    </Row>
  )
}
